<template>
  <v-dialog v-model="openMode" fullscreen transition="dialog-fade-transition" content-class="plans-dialog">
    <!-- Modern Header -->
    <v-app-bar flat dark color="#0d2c6d" height="50" class="plans-header">
      <v-btn icon dark @click="openMode = false" class="close-btn">
        <v-icon>mdi-close</v-icon>
      </v-btn>
      <v-toolbar-title class="plans-title">חבילות</v-toolbar-title>
      <v-spacer></v-spacer>
    </v-app-bar>

    <v-card class="plans-container systembody">
      <div class="plans-content">
        <div class="plans-intro">
          <h1 class="plans-heading">בחר את החבילה המתאימה לך</h1>
          <p class="plans-subheading">אנו מציעים מגוון חבילות המותאמות לצרכים שונים</p>
        </div>

        <div class="plans-grid">
          <v-row justify="center" style="box-shadow: inset 0px 1px 2px rgba(0, 0, 0, 0.1);">
            <v-col cols="auto" v-for="(item, index) in plans" :key="index">
              <div class="plan-card" :class="{
                'plan-card-current': item.isCurrent,
                'plan-card-featured': index === 1,
                'plan-card-ravkol': ['free', 'ravkol'].includes(item.bill_type)
              }">
                <div class="plan-header" :class="index % 2 === 0 ? 'primary-plan' : 'secondary-plan'">
                  <div class="plan-name-container">
                    <h2 class="plan-name">{{ item.name }}</h2>
                  </div>
                  <div class="plan-price">
                    <span class="currency">₪</span>
                    <span class="amount">{{ item.price }}</span>
                    <span class="period" v-if="item.price > 0">/חודש</span>
                    <span class="free-label" v-else>חינם</span>
                  </div>
                </div>

                <div class="plan-body">
                  <div class="plan-features">
                    <div class="feature-item">
                      <v-icon :color="index % 2 === 0 ? '#0d2c6d' : '#fdbd29'"
                        class="feature-icon">mdi-account-group</v-icon>
                      <div class="feature-text">
                        <span class="feature-value">{{ item.contacts }}</span>
                        <span class="feature-label">אנשי קשר</span>
                      </div>
                    </div>

                    <div class="feature-item">
                      <v-icon :color="index % 2 === 0 ? '#0d2c6d' : '#fdbd29'"
                        class="feature-icon">mdi-database</v-icon>
                      <div class="feature-text">
                        <span class="feature-value">{{ item.storage }}GB</span>
                        <span class="feature-label">אחסון</span>
                      </div>
                    </div>

                    <div class="feature-item">
                      <v-icon :color="index % 2 === 0 ? '#0d2c6d' : '#fdbd29'"
                        class="feature-icon">mdi-broadcast</v-icon>
                      <div class="feature-text">
                        <span class="feature-value">{{ item.channels }}</span>
                        <span class="feature-label">ערוצים</span>
                      </div>
                    </div>
                  </div>

                  <!-- מה החבילה כוללת - מחושב בשרת לפי המערכת של המשתמש (perks) -->
                  <ul v-if="item.perks" class="plan-perks">
                    <li :class="item.perks.noAds ? 'perk-yes' : 'perk-no'">
                      <v-icon small class="perk-icon">{{ item.perks.noAds ? 'mdi-check-circle' : 'mdi-minus-circle-outline' }}</v-icon>
                      <span>{{ item.perks.noAds ? 'בלי פרסומות' : 'פרסומות למתקשרים בלי מנוי רב קול' }}</span>
                    </li>
                    <li v-if="item.perks.ai" class="perk-yes">
                      <v-icon small class="perk-icon">mdi-check-circle</v-icon>
                      <span>{{ item.perks.ai === 'all' ? 'עוזר קולי לכל המתקשרים' : 'עוזר קולי למנויי רב קול' }}</span>
                    </li>
                    <li :class="item.perks.keptWhenUnused ? 'perk-yes' : 'perk-no'">
                      <v-icon small class="perk-icon">{{ item.perks.keptWhenUnused ? 'mdi-check-circle' : 'mdi-minus-circle-outline' }}</v-icon>
                      <span>{{ item.perks.keptWhenUnused ? 'המערכת נשמרת גם בלי שימוש' : 'נמחקת אחרי 60 יום בלי שימוש' }}</span>
                    </li>
                  </ul>

                  <!-- החבילה הנוכחית: מוצגת להשוואה, בלי אפשרות לבחור אותה -->
                  <v-btn v-if="item.isCurrent" disabled depressed class="current-btn">
                    <v-icon left small>mdi-check</v-icon>
                    <span>החבילה הנוכחית</span>
                  </v-btn>

                  <!-- Different button based on plan price -->
                  <v-btn v-else-if="item.price > 0" :color="index % 2 === 0 ? '#0d2c6d' : '#fdbd29'" :dark="index % 2 === 0"
                    @click="openPayDialog(item, index)" class="purchase-btn" elevation="2" :loading="progressShow">
                    <span>לרכישה</span>
                    <v-icon right>mdi-arrow-left</v-icon>
                  </v-btn>

                  <v-btn v-else color="purple" dark @click="switchToPlan(item, index)" class="switch-btn" elevation="2"
                    :loading="progressShow">
                    <span>החלף</span>
                    <v-icon right>mdi-swap-horizontal</v-icon>
                  </v-btn>
                </div>

                <div v-if="index === 1" class="popular-badge">
                  <span>פופולרי</span>
                </div>

                <!-- Rav-Kol Only Badge -->
                <div v-if="['free', 'ravkol'].includes(item.bill_type)" class="ravkol-only-badge">
                  <v-icon small>mdi-account-star</v-icon>
                  <span>חינמית</span>
                </div>
              </div>
            </v-col>
          </v-row>
          <p class="perks-note">מוצגות התכונות שזמינות במערכת שלך</p>
        </div>

        <div class="plans-info" style="display: flex; justify-content: center;">
          <div class="info-card">
            <v-icon color="#0d2c6d" size="36">mdi-shield-check</v-icon>
            <h3>תשלום מאובטח</h3>
            <p>כל העסקאות מאובטחות ומוצפנות</p>
          </div>

          <!-- <div class="info-card">
            <v-icon color="#0d2c6d" size="36">mdi-currency-usd</v-icon>
            <h3>החזר כספי</h3>
            <p>30 יום אחריות החזר כספי</p>
          </div>
          
          <div class="info-card">
            <v-icon color="#0d2c6d" size="36">mdi-headset</v-icon>
            <h3>תמיכה 24/7</h3>
            <p>תמיכה טכנית זמינה בכל עת</p>
          </div> -->
        </div>
      </div>
    </v-card>

    <PayDialog v-model="payDialog" v-if="payDialog" :planToPay="planToPay" />
    <SnackBar v-model="snackbar" :snacbarText="snacbarText" :snackbarColorBt="snackbarColorBt"></SnackBar>
  </v-dialog>
</template>

<script>
import ApiServices from '@/services/api.service'
import SnackBar from '@/components/widgets/snackBar.vue'
import PayDialog from '@/components/settingspage/dialogs/PayDialog'
import Auth from '@/services/auth.service.js'

export default {
  props: {
    value: { type: Boolean, default: false },
  },
  components: {
    SnackBar,
    PayDialog
  },
  data: () => ({
    progressShow: false,
    snackbar: false,
    snackbarColorBt: "green",
    snacbarText: "",
    planToPay: {},
    plans: [],
    payDialog: false,
    lineNumber: "",
    userDetails: {}
  }),

  methods: {
    actionConfirmed() {
      this.deleteItem();
    },

    async getAllPlans() {
      try {
        this.progressShow = true;
        let token = localStorage.getItem("token");
        let api = process.env.VUE_APP_BASE_URL + `/settings/get_all_plans`;
        const res = await fetch(api, ApiServices.requestOptions("GET", "", token));
        const jsonObject = await res.json();

        if (res.status >= 400) {
          this.showSnackBar("שגיאה בקבלת נתונים", "red");
        } else if (res.status === 200) {
          this.plans = jsonObject;
          console.log(this.plans);
        }
      } catch (error) {
        this.showSnackBar("Error get files: " + error, "red");
      } finally {
        this.progressShow = false;
      }
    },

    closeDelete() {
      this.openMode = false;
    },

    showSnackBar(message, color) {
      this.snacbarText = message;
      this.snackbarColorBt = color;
      this.snackbar = true
    },

    async openPayDialog(item, index) {
      try {
        this.progressShow = true
        let MyJSON = JSON.stringify({ planDetails: item, userDetails: this.userDetails, lineNumber: this.lineNumber });
        let api = process.env.VUE_APP_BASE_URL + "/pay/get_pay_page"
        let token = localStorage.getItem("token");

        const response = await fetch(api, ApiServices.requestOptions("POST", MyJSON, token));
        const jsonObject = await response.json();

        if (response.status >= 400) {
          this.showSnackBar("שגיאת: " + jsonObject.message, "red");
        } else if (response.status === 200) {
          console.log(jsonObject.data);
          // Convert the string of key-value pairs into an object
          const params = new URLSearchParams(jsonObject.data);
          // Get the 'url' value
          const url = params.get('url');
          window.location.href = url;
        }
      } catch (error) {
        this.showSnackBar("Error: " + error, "red");
      } finally {
        this.progressShow = false;
      }
    },

    async switchToPlan(item, index) {
      try {
        this.progressShow = true;
        let token = localStorage.getItem("token");
        let MyJSON = JSON.stringify({ planDetails: item });
        let api = process.env.VUE_APP_BASE_URL + "/settings/switch_to_ravkol_plan";

        const response = await fetch(api, ApiServices.requestOptions("POST", MyJSON, token));
        const jsonObject = await response.json();

        if (response.status >= 400) {
          this.showSnackBar("שגיאה בהחלפת החבילה: " + jsonObject.message, "red");
        } else if (response.status === 200) {
          this.showSnackBar("החבילה הוחלפה בהצלחה!", "green");
          // Close dialog after successful switch
          setTimeout(() => {
            this.openMode = false;
            // Optionally refresh the parent component
            this.$emit('planSwitched');
          }, 1500);
        }
      } catch (error) {
        this.showSnackBar("Error: " + error, "red");
      } finally {
        this.progressShow = false;
      }
    },

    async getIvrOwnerDetails() {
      try {
        this.progressShow = true;
        let token = localStorage.getItem("token");
        let api = process.env.VUE_APP_BASE_URL + `/account/get_account_details`;
        const res = await fetch(api, ApiServices.requestOptions("GET", "", token));
        const jsonObject = await res.json();

        if (res.status >= 400) {
          this.showSnackBar("שגיאה בקבלת נתונים", "red");
        } else if (res.status === 200) {
          this.userDetails = jsonObject;
        }
      } catch (error) {
        this.showSnackBar("Error get files: " + error, "red");
      } finally {
        this.progressShow = false;
      }
    },
  },

  computed: {
    openMode: {
      get() {
        return this.value
      },
      set(v) {
        return this.$emit('input', v)
      }
    },
  },

  mounted() {
    this.lineNumber = Auth.getLineNumber();
    this.getAllPlans();
    this.getIvrOwnerDetails();
  },
}
</script>

<style scoped>
/* מה החבילה כוללת: קו דק מתחת לנתונים הבסיסיים, ושורה לכל תכונה */
/* החבילה הנוכחית: מסגרת כחולה, וכפתור שאי אפשר ללחוץ עליו */
.plan-card-current {
  box-shadow: 0 0 0 2px #0d2c6d !important;
}

.current-btn {
  width: 100%;
  font-weight: 600;
  letter-spacing: 0;
}

.plan-perks {
  list-style: none;
  padding: 14px 0 0;
  margin: 4px 0 18px;
  border-top: 1px solid rgba(13, 44, 109, 0.12);
  text-align: right;
}

.plan-perks li {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 0.92rem;
  line-height: 1.45;
  padding: 4px 0;
}

.plan-perks .perk-icon {
  margin-top: 2px;
  flex-shrink: 0;
}

.plan-perks .perk-yes {
  color: #0d2c6d;
  font-weight: 500;
}

.plan-perks .perk-yes .perk-icon {
  color: #0d2c6d !important;
}

.plan-perks .perk-no {
  color: #8a93a6;
}

.plan-perks .perk-no .perk-icon {
  color: #b7bdca !important;
}

.perks-note {
  text-align: center;
  font-size: 0.82rem;
  color: #8a93a6;
  margin: 10px 0 0;
}
.plans-dialog {
  background-color: #f8f9fa;
}

.plans-header {
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.plans-title {
  font-size: 1.4rem;
  font-weight: 600;
}

.plans-container {
  background-color: #f8f9fa;
  height: 100%;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

.plans-content {
  max-width: 1550px;
  margin: 0 auto;
  padding: 32px 16px;
  width: 100%;
}

.plans-intro {
  text-align: center;
  margin-bottom: 30px;
}

.plans-heading {
  font-size: 2.2rem;
  font-weight: 700;
  color: #0d2c6d;
  margin-bottom: 12px;
}

.plans-subheading {
  font-size: 1.2rem;
  color: #6A707E;
  max-width: 600px;
  margin: 0 auto;
}

.plans-slider {
  padding: 16px 0;
}

.plan-card {
  width: 320px;
  border-radius: 16px;
  overflow: hidden;
  background-color: white;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  margin: 16px 16px;
  transition: all 0.3s ease;
  position: relative;
  border: 2px solid transparent;
}

.plan-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.12);
}

.plan-card-featured {
  transform: scale(1.05);
  border-color: #fdbd29;
  z-index: 1;
}

.plan-card-featured:hover {
  transform: translateY(-8px) scale(1.05);
}

.plan-card-ravkol {
  border-color: #9c27b0;
  border-width: 3px;
}

.plan-header {
  padding: 24px;
  text-align: center;
  color: white;
}

.plan-name-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
}

.primary-plan {
  background: linear-gradient(135deg, #0d2c6d 0%, #1a4ba5 100%);
}

.secondary-plan {
  background: linear-gradient(135deg, #fdbd29 0%, #ffcf5c 100%);
  color: #0d2c6d;
}

.plan-name {
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0;
}

.ravkol-chip {
  font-size: 0.7rem;
  height: 20px;
}

.plan-price {
  display: flex;
  align-items: baseline;
  justify-content: center;
}

.currency {
  font-size: 1.2rem;
  font-weight: 600;
  margin-right: 4px;
}

.amount {
  font-size: 3rem;
  font-weight: 700;
  line-height: 1;
}

.period {
  font-size: 0.9rem;
  opacity: 0.8;
  margin-right: 4px;
}

.free-label {
  font-size: 1.5rem;
  font-weight: 600;
  opacity: 0.9;
}

.plan-body {
  padding: 24px;
}

.plan-features {
  margin-bottom: 24px;
}

.feature-item {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.feature-item:last-child {
  margin-bottom: 0;
  padding-bottom: 0;
  border-bottom: none;
}

.feature-icon {
  margin-left: 16px;
  font-size: 24px;
}

.feature-text {
  display: flex;
  flex-direction: column;
}

.feature-value {
  font-size: 1.2rem;
  font-weight: 700;
  color: #333;
}

.feature-label {
  font-size: 0.9rem;
  color: #6A707E;
}

.purchase-btn,
.switch-btn {
  width: 100%;
  height: 48px;
  font-size: 1rem;
  font-weight: 600;
  text-transform: none;
  letter-spacing: 0;
  border-radius: 8px;
}

.switch-btn {
  background: linear-gradient(135deg, #9c27b0 0%, #ba68c8 100%);
}

.popular-badge {
  position: absolute;
  top: 12px;
  left: 12px;
  background-color: #0d2c6d;
  color: white;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.ravkol-only-badge {
  position: absolute;
  top: 12px;
  right: 12px;
  background-color: #9c27b0;
  color: white;
  padding: 4px 8px;
  border-radius: 20px;
  font-size: 0.7rem;
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  display: flex;
  align-items: center;
  gap: 4px;
}

.plans-info {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 24px;
  margin-top: 32px;
}

.info-card {
  background-color: white;
  border-radius: 12px;
  padding: 24px;
  text-align: center;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
}

.info-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
}

.info-card h3 {
  margin: 16px 0 8px;
  color: #0d2c6d;
  font-size: 1.2rem;
  font-weight: 600;
}

.info-card p {
  color: #6A707E;
  font-size: 0.9rem;
  margin: 0;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .plans-heading {
    font-size: 1.8rem;
  }

  .plans-subheading {
    font-size: 1rem;
  }

  .plan-card {
    width: 280px;
  }

  .plan-name {
    font-size: 1.3rem;
  }

  .amount {
    font-size: 2.5rem;
  }
}

@media (max-width: 600px) {
  .plans-content {
    padding: 24px 12px;
  }

  .plans-heading {
    font-size: 1.6rem;
  }

  .plan-card {
    width: 260px;
    margin: 0 8px;
  }

  .plan-header {
    padding: 20px;
  }

  .plan-body {
    padding: 20px;
  }

  .info-card {
    padding: 20px;
  }
}

/* Dialog transition */
.dialog-fade-transition-enter-active,
.dialog-fade-transition-leave-active {
  transition: opacity 0.3s ease;
}

.dialog-fade-transition-enter,
.dialog-fade-transition-leave-to {
  opacity: 0;
}
</style>